import { useEffect, useState } from "react";
import { Plus, Lock, AlertTriangle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { fmt } from "./piecework/shared";
import { AgreementForm } from "./piecework/AgreementForm";
import { AgreementCard } from "./piecework/AgreementCard";
import { TerminateModal } from "./piecework/TerminateModal";
import { fmtH } from "../lib/format";

export function PieceworkPanel({ jobId, cards, laborBase, onChanged, refreshKey = 0 }) {
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(null);
    const [terminating, setTerminating] = useState(null);

    const load = () => api.get(`/jobs/${jobId}/piecework`).then((r) => setData(r.data)).catch(() => setData({ agreements: [], summary: {} }));
    useEffect(() => { load(); }, [jobId, refreshKey]); // eslint-disable-line
    const changed = () => { load(); onChanged?.(); };

    if (!data) return <div className="text-slate-500 text-xs uppercase tracking-widest">Načítání…</div>;
    const s = data.summary || {};
    const hasIssues = s.open_claims > 0 || !!s.reservations;
    const over = (s.budget_remaining || 0) < 0;
    const quickAdd = async (u, pay_type) => {
        if (pay_type === "hodinova" && !u.craftsman_id) { toast.error("Hodinově lze přidat jen řemeslníka s kartou"); return; }
        if (pay_type === "ukol") { setEditing({ craftsman_id: u.craftsman_id || "", name: u.name, pay_type: "ukol" }); return; }
        try { await api.post(`/jobs/${jobId}/piecework`, { craftsman_id: u.craftsman_id, name: u.name, pay_type: "hodinova", hourly_rate: null, notes: "" }); toast.success(`${u.name} přidán hodinově — dosavadní hodiny se započítají`); changed(); }
        catch (e) { toast.error(e.response?.data?.detail || "Přidání selhalo"); }
    };

    return (
        <div className="space-y-4" data-testid="piecework-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Stat l="Dohodnuto celkem" v={fmt(s.agreed_total)} testid="pw-agreed" sub="aktivní dohody + vyúčtování ukončených + hodinově" />
                <Stat l="Vyplaceno (dohody)" v={fmt(s.paid_total)} testid="pw-paid" cls="text-emerald-700" />
                <Stat l="Zbývá z dohod" v={fmt(s.remaining)} testid="pw-remaining" cls={s.remaining > 0 ? "text-red-700" : "text-emerald-700"} />
                <Stat l="Hodinově (hodiny × sazba)" v={fmt(s.hourly_cost)} testid="pw-hourly-cost" sub={`${fmtH(s.hourly_hours)} · jde do uzávěrky`} />
                <Stat l="Zbývá z původního rozpočtu práce" v={over ? `Přečerpáno o ${fmt(Math.abs(s.budget_remaining))}` : fmt(s.budget_remaining)} testid="pw-budget-remaining"
                      cls={over ? "text-red-700" : "text-emerald-700"} sub={`původně dohodnuto ${fmt(s.original_total)}`} />
                <Stat l="Odpracováno celkem" v={fmtH(s.hours)} testid="pw-hours" sub={s.hourly_equivalent != null ? `úkol orientačně ${fmt(s.hourly_equivalent)}/h` : undefined} />
            </div>

            {s.unassigned?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-400 px-4 py-3 space-y-2" data-testid="pw-unassigned">
                    <div className="flex items-center gap-2 text-xs font-semibold text-yellow-900"><AlertTriangle className="w-4 h-4" /> Nepřiřazení řemeslníci — jejich hodiny se zatím jen evidují. Přidejte je jako Úkol nebo Hodinově (dosavadní hodiny se započítají zpětně).</div>
                    {s.unassigned.map((u) => (
                        <div key={u.craftsman_id || u.name} className="flex items-center justify-between gap-2 flex-wrap text-sm" data-testid={`pw-unassigned-${u.craftsman_id || u.name}`}>
                            <span><span className="font-semibold">{u.name}</span> <span className="text-slate-600 tabular-data">· {fmtH(u.hours)}</span>{!u.craftsman_id && <span className="text-xs text-slate-500"> (bez karty)</span>}</span>
                            <span className="inline-flex gap-1">
                                <button onClick={() => quickAdd(u, "hodinova")} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-sky-700 hover:bg-sky-800 text-white rounded-sm inline-flex items-center gap-1" data-testid={`pw-unassigned-hourly-${u.craftsman_id || u.name}`}><UserPlus className="w-3 h-3" /> Přidat jako Hodinově</button>
                                <button onClick={() => quickAdd(u, "ukol")} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-900 text-slate-900 hover:bg-white rounded-sm" data-testid={`pw-unassigned-ukol-${u.craftsman_id || u.name}`}>Přidat jako Úkol</button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {!s.handover_signed && (
                <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 px-4 py-2" data-testid="pw-final-locked-note">
                    <Lock className="w-3.5 h-3.5" /> Doplatek se odemkne po podepsaném předávacím protokolu (záložka Předání &amp; Záruka).
                </div>
            )}
            {hasIssues && (
                <div className="flex items-start gap-2 text-xs text-red-800 bg-red-50 border border-red-300 px-4 py-2" data-testid="pw-claims-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <div>
                        {s.open_claims > 0 && <div>Na zakázce {s.open_claims === 1 ? "je otevřená reklamace" : `jsou otevřené reklamace (${s.open_claims})`} — před výplatou doplatku prověřte.</div>}
                        {s.reservations && <div>V předávacím protokolu jsou zapsané vady / výhrady: <span className="italic">{s.reservations}</span></div>}
                    </div>
                </div>
            )}

            <div className="bg-white border border-slate-200">
                <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Řemeslníci na zakázce — dohody a hodinové sazby</div>
                    {!editing && (
                        <button onClick={() => setEditing({})} data-testid="pw-add" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1.5">
                            <Plus className="w-3.5 h-3.5" /> Přidat řemeslníka / partu
                        </button>
                    )}
                </div>
                {editing && (
                    <AgreementForm jobId={jobId} cards={cards} laborBase={laborBase ?? s.labor_price_base} initial={editing}
                                   onClose={() => setEditing(null)} onSaved={() => { setEditing(null); changed(); }} />
                )}
                {data.agreements.length === 0 && !editing && (
                    <div className="p-10 text-center text-slate-400 text-sm">Zatím nikdo. Přidejte řemeslníka nebo partu — s dohodou za dílo, nebo hodinově.</div>
                )}
                {data.agreements.map((a) => (
                    <AgreementCard key={a.id} a={a} jobId={jobId} summary={s} onEdit={() => setEditing(a)} onTerminate={() => setTerminating(a)} onChanged={changed} />
                ))}
            </div>
            {terminating && <TerminateModal jobId={jobId} a={terminating} onClose={() => setTerminating(null)} onDone={() => { setTerminating(null); changed(); }} />}
        </div>
    );
}

function Stat({ l, v, cls = "", testid, sub }) {
    return (
        <div className="bg-white border border-slate-200 p-3">
            <div className="label-eyebrow">{l}</div>
            <div className={`font-display text-lg md:text-xl font-black tabular-data mt-1 ${cls}`} data-testid={testid}>{v}</div>
            {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}
